import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const WELCOME_MSG = {
  sent: "Welcome text sent",
  no_phone: "No phone number — welcome text not sent",
  texting_not_configured: "Texting isn't set up — welcome text not sent",
  turned_off: "Welcome text is switched off in Alerts",
};
const today = () => new Date().toISOString().slice(0, 10);

const bulkToast = (d, sendWelcome) => {
  toast.success(`${d.marked} member${d.marked === 1 ? "" : "s"} marked as first-timer${d.marked === 1 ? "" : "s"}`);
  if (!sendWelcome) return;
  if (d.welcome_sent) toast.success(`${d.welcome_sent} welcome text${d.welcome_sent === 1 ? "" : "s"} sent`);
  if (d.no_phone) toast.info(`${d.no_phone} skipped — no phone number`);
  if (d.welcome_skipped) toast.info(`${d.welcome_skipped} welcome text${d.welcome_skipped === 1 ? "" : "s"} not sent (texting off or not set up)`);
};

// `members`: one member (single mark/unmark) or several (bulk mark).
export const FirstTimerMarkDialog = ({ members, bulkMode, onClose, onSaved }) => {
  const list = members || [];
  const bulk = !!bulkMode;
  const one = list[0];
  const withPhone = list.filter((m) => m.phone).length;
  const unmark = !bulk && !!one?.is_first_timer;
  const [date, setDate] = useState(today());
  const [sendWelcome, setSendWelcome] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!list.length) return;
    setDate((!bulk && (one.first_timer_at || "").slice(0, 10)) || today());
    setSendWelcome(withPhone > 0);
  }, [members]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      if (bulk) {
        const { data } = await api.post("/care/first-timer/bulk", { member_ids: list.map((m) => m.id), visit_date: date, send_welcome: sendWelcome });
        bulkToast(data, sendWelcome);
      } else {
        const { data } = await api.patch(`/care/members/${one.id}/first-timer`, unmark ? { is_first_timer: false } : { is_first_timer: true, visit_date: date, send_welcome: sendWelcome });
        toast.success(unmark ? `${one.name} is no longer tagged as a first-timer` : `${one.name} marked as a first-timer`);
        if (data.welcome) (data.welcome === "sent" ? toast.success : toast.info)(WELCOME_MSG[data.welcome]);
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const who = bulk ? `These ${list.length} members` : one?.name;
  return (
    <Dialog open={list.length > 0} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="first-timer-dialog">
        <DialogHeader>
          <DialogTitle>{unmark ? "Remove first-timer tag" : bulk ? `Mark ${list.length} as first-timers` : "Mark as first-timer"}</DialogTitle>
          <DialogDescription>
            {unmark ? `${who} will no longer be counted in the First-timer report or receive the second-visit nudge.`
              : `${who} will get the First-timer badge, be counted in the report and receive the second-visit nudge if not reached.`}
          </DialogDescription>
        </DialogHeader>
        {!unmark && (
          <div className="space-y-4">
            <label className="block text-sm font-semibold">{bulk ? "First visit date (for all)" : "First visit date"}
              <input type="date" data-testid="first-timer-date-input" value={date} max={today()} onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-4 py-2.5 text-sm font-normal outline-none focus:ring-2 focus:ring-[#0B5E4E]/20" />
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" data-testid="first-timer-send-welcome" checked={sendWelcome} disabled={!withPhone} onChange={(e) => setSendWelcome(e.target.checked)} className="h-4 w-4 mt-0.5 accent-[#0B5E4E]" />
              <span>Send the welcome text now
                <span className="block text-xs" data-testid="first-timer-phone-note" style={{ color: "var(--sj-text-3)" }}>
                  {!withPhone ? "No phone number on record" : bulk ? `${withPhone} of ${list.length} have a phone number` : ""}
                </span>
              </span>
            </label>
          </div>
        )}
        <DialogFooter>
          <button data-testid="first-timer-cancel" onClick={onClose} className="rounded-full px-5 py-2 text-sm font-semibold border" style={{ borderColor: "var(--sj-border)" }}>Cancel</button>
          <button data-testid="first-timer-save" onClick={save} disabled={saving || (!unmark && !date)} className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: unmark ? "#C0392B" : "var(--sj-primary)" }}>
            {saving ? "Saving…" : unmark ? "Remove tag" : bulk ? `Mark ${list.length} first-timers` : "Mark first-timer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
